import type { UseConfirmationDialogParams } from '@openx/components-core/lib/Dialog/ConfirmationDialog/types.ts';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { EditConfirmationModalContent } from './components/EditConfirmationModalContent.tsx';

export type PackageEditConfirmationMode = 'default' | 'disable_rbo' | 'remove_retargeting';

export type ShowConfirmationModalParams = {
  deals: { name: string; action: string }[];
  mode?: PackageEditConfirmationMode;
  isRBODynamic: boolean;
  hasMore: boolean;
  confirmationLabel?: string;
  confirmationButtonColor?: UseConfirmationDialogParams['confirmationButtonColor'];
  onConfirmed: () => void;
  handleClonePackage: () => void;
};

export const useEditConfirmationModal = () => {
  const { t } = useTranslation();
  const confirmationDialog = useConfirmationDialog();

  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();

  const showConfirmationModal = useCallback(
    ({
      deals,
      hasMore,
      mode = 'default',
      isRBODynamic,
      confirmationLabel = t('Save'),
      confirmationButtonColor = 'primary',
      onConfirmed,
      handleClonePackage,
    }: ShowConfirmationModalParams) => {
      confirmationDialog.open({
        confirmationButtonColor,
        confirmationLabel,
        content: (
          <EditConfirmationModalContent deals={deals} hasMore={hasMore} mode={mode} isRBODynamic={isRBODynamic} />
        ),
        customContent: true,
        extraBtnLabel: t('Clone Package'),
        onCancel: () => {
          confirmationDialog.close();
        },
        onConfirmed: () => {
          // Close before onConfirmed so a chained confirmation (e.g. remove retargeting →
          // related-deals / RBO) is not immediately dismissed by this close().
          confirmationDialog.close();
          onConfirmed();
        },
        onExtraAction: () => {
          handleClonePackage();
          confirmationDialog.close();
        },
        showExtraBtn: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.PACKAGE_CREATE)),
        title: t('Confirmation'),
      });
    },
    [confirmationDialog, isAllowed, t]
  );

  return { showConfirmationModal };
};
